import { useState, useCallback } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Box, TextField, Button, CircularProgress } from "@mui/material";
import { toast } from "react-hot-toast";
import { changePassword } from "../../api/auth";
import AuthPage, {
  authTextFieldSx,
  authSubmitButtonSx,
} from "../auth-page/auth-page";
import PasswordRequirements, {
  passwordMeetsRequirements,
} from "../auth-page/passwordRequirements";

export default function ChangePasswordForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state && location.state.email;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    oldPassword: "",
    password: "",
    confirmedPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "error",
    duration: 2000,
  });

  const showNotification = useCallback(
    (message, severity = "error", duration = 2000) => {
      setNotification({ open: true, message, severity, duration });
    },
    []
  );

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.oldPassword) {
      newErrors.oldPassword = "Password is required";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!passwordMeetsRequirements(formData.password)) {
      newErrors.password = "Password does not meet the requirements.";
    }
    if (!formData.confirmedPassword) {
      newErrors.confirmedPassword = "Password is required";
    } else if (formData.confirmedPassword !== formData.password) {
      newErrors.confirmedPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      try {
        setLoading(true);
        const result = await changePassword(
          email,
          formData.oldPassword,
          formData.password
        );
        setLoading(false);

        if (result.isOk) {
          toast.success(
            "Password changed successfully. Please log in with your new password.",
            { duration: 3000 }
          );
          navigate("/login");
        } else {
          showNotification(result.message, "error", 2000);
        }
      } catch {
        setLoading(false);
        showNotification(
          "Error activating user, if having issues logging in please contant IT.",
          "error",
          2000
        );
      }
    },
    [email, formData, navigate, showNotification, validateForm]
  );

  if (!email) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthPage
      subtitle="Set a new password before continuing."
      notification={notification}
      onCloseNotification={() =>
        setNotification((prev) => ({ ...prev, open: false }))
      }
    >
      <Box
        component="form"
        onSubmit={onSubmit}
        noValidate
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1.75,
        }}
      >
        <TextField
          name="oldPassword"
          type="password"
          placeholder="Old Password"
          variant="outlined"
          value={formData.oldPassword}
          onChange={handleInputChange("oldPassword")}
          error={!!errors.oldPassword}
          helperText={errors.oldPassword}
          disabled={loading}
          fullWidth
          autoComplete="current-password"
          InputLabelProps={{ shrink: false }}
          sx={authTextFieldSx}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <TextField
            name="password"
            type="password"
            placeholder="Password"
            variant="outlined"
            value={formData.password}
            onChange={handleInputChange("password")}
            error={!!errors.password}
            helperText={errors.password}
            disabled={loading}
            fullWidth
            autoComplete="new-password"
            InputLabelProps={{ shrink: false }}
            sx={authTextFieldSx}
          />
          <PasswordRequirements password={formData.password} />
        </Box>
        <TextField
          name="confirmedPassword"
          type="password"
          placeholder="Confirm Password"
          variant="outlined"
          value={formData.confirmedPassword}
          onChange={handleInputChange("confirmedPassword")}
          error={!!errors.confirmedPassword}
          helperText={errors.confirmedPassword}
          disabled={loading}
          fullWidth
          autoComplete="new-password"
          InputLabelProps={{ shrink: false }}
          sx={authTextFieldSx}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          fullWidth
          sx={authSubmitButtonSx}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "inherit" }} />
          ) : (
            "Continue"
          )}
        </Button>
      </Box>
    </AuthPage>
  );
}
